const STOP = new Set([
  'how',
  'to',
  'do',
  'i',
  'a',
  'an',
  'the',
  'create',
  'new',
  'make',
  'open',
  'where',
  'is',
  'como',
  'cómo',
  'crear',
  'hacer',
  'una',
  'un',
  'el',
  'la',
  'los',
  'las',
  'de',
  'del',
  'para',
  'que',
  'es',
  'en',
  'donde',
  'dónde',
  'abrir',
  'busca',
  'buscar',
  'quiero',
  'necesito',
  'puedo',
  'nuevo',
  'nueva',
])

const CREATE_WORDS = new Set(['create', 'crear', 'new', 'nuevo', 'nueva', 'hacer', 'record', 'registrar', 'registro', 'agregar', 'agrego', 'add', 'anadir'])

export function fold(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function haystack(feature) {
  return fold([feature.label.en, feature.label.es, feature.group.en, feature.group.es, ...(feature.keywords || [])].join(' '))
}

export function scoreFeature(query, feature) {
  const q = fold(query)
  const rawTokens = q.split(' ').filter((token) => token.length > 1)
  const tokens = rawTokens.filter((token) => !STOP.has(token))
  const usable = tokens.length ? tokens : rawTokens
  if (!usable.length) return 0
  if (feature.id === 'create-invoice' && /\b(vendor|proveedor)\b/.test(q)) return 0
  if ((feature.id === 'create-product' || feature.id === 'products') && /\b(transfer|transfers|transferir|transferencia|transferencias)\b/.test(q)) return 0
  const label = fold(`${feature.label.en} ${feature.label.es}`)
  const bag = haystack(feature)
  const hits = usable.filter((token) => bag.includes(token) && !CREATE_WORDS.has(token))
  if (!hits.length) return 0

  let score = 10 + hits.length
  const hitQuery = hits.join(' ')
  if (label === q || label === hitQuery) score += 100
  else if (label.startsWith(q) || label.startsWith(hitQuery)) score += 60
  else if (label.includes(q) || (hitQuery && label.includes(hitQuery))) score += 40
  score += hits.filter((token) => label.includes(token)).length * 8
  if (feature.kind === 'create' && rawTokens.some((token) => CREATE_WORDS.has(token))) score += 12
  const phrases = (feature.keywords || []).map((word) => fold(word)).filter((word) => word.includes(' ') && q.includes(word))
  score += phrases.length * 50
  return score
}

export function searchFeatures(query, features, limit = 8) {
  const q = fold(query)
  if (!q) return []
  return features
    .map((feature) => ({ feature, score: scoreFeature(q, feature) }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score || a.feature.label.en.localeCompare(b.feature.label.en))
    .slice(0, limit)
    .map((row) => row.feature)
}

function answerLanguage(question, folded, locale) {
  if (/[áéíóúñ]/i.test(question) || /\b(como|crear|factura|facturar|facturo|donde|quiero|hacer|cotizacion|cotizar|agregar|agrego|anadir|anado|inventario|transferencia|transferir|transfiero|almacen|almacenes|proveedor|proveedores|recibo|recepcion|ajuste|ajustes|necesito|puedo|registrar|registro|cobro|cobros|producto|productos|compra|compras|venta|ventas|mercancia|pagar|pago|pagos|articulo|articulos|cliente|clientes|bodega|bodegas|existencia|existencias)\b/.test(folded)) return 'es'
  return String(locale || 'en').toLowerCase().startsWith('es') ? 'es' : 'en'
}

function rank(query, features) {
  return features
    .map((feature) => ({ feature, score: scoreFeature(query, feature) }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score || a.feature.label.en.localeCompare(b.feature.label.en))
}

function publicFeature(feature) {
  return {
    id: feature.id,
    label: feature.label,
    group: feature.group,
  }
}

function stepsFor(feature, lang) {
  if (feature.steps) return feature.steps[lang] || feature.steps.en
  if (lang === 'es') return [`Abre ${feature.group.es}.`, `Elige ${feature.label.es}.`]
  return [`Open ${feature.group.en}.`, `Choose ${feature.label.en}.`]
}

/**
 * Answers a how-to question using only functions enabled for this company.
 * A match that the company cannot open returns no steps and no path.
 */
export function answerCompanyQuestion(question, { available = [], unavailable = [], locale = 'en' } = {}) {
  const folded = fold(question)
  const tokens = folded.split(' ').filter((token) => token.length > 1 && !STOP.has(token))
  const reduced = tokens.join(' ')
  const query = reduced || folded
  if (!query) return { found: false, available: false }

  const lang = answerLanguage(question, folded, locale)
  const best = rank(query, available)[0]
  const blocked = rank(query, unavailable)[0]
  const wantsCreate = /\b(create|crear|nuevo|nueva|new|hacer)\b/.test(folded)

  const blockedCreateOutranks =
    wantsCreate &&
    blocked &&
    blocked.feature.kind === 'create' &&
    (!best || best.feature.kind !== 'create' || blocked.score > best.score)
  if (blockedCreateOutranks) {
    return { found: true, available: false, feature: publicFeature(blocked.feature) }
  }

  if (!best) {
    if (!blocked) return { found: false, available: false }
    return { found: true, available: false, feature: publicFeature(blocked.feature) }
  }

  return {
    found: true,
    available: true,
    feature: best.feature,
    steps: stepsFor(best.feature, lang),
    related: rank(query, available)
      .slice(1, 3)
      .map((row) => row.feature),
  }
}
